import { ArrowRight, DollarSign, Users, ExternalLink } from 'lucide-react';
import ThemeToggle from '../ThemeToggle';

interface Props { navigate: (path: string) => void }

const MAIN_SITE = 'https://oasisautokonnect.com';

export default function Landing({ navigate }: Props) {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-inter">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href={MAIN_SITE} className="flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white hover:opacity-80">
            <span className="text-cyan-600 dark:text-cyan-400">OAK</span>
            <span className="text-base font-medium text-slate-700 dark:text-slate-300">Affiliates</span>
          </a>
          <nav className="flex items-center gap-2">
            <a
              href={MAIN_SITE}
              className="hidden sm:flex items-center gap-1 px-3 py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-cyan-600 dark:hover:text-cyan-400"
            >
              Main Site <ExternalLink className="w-3 h-3" />
            </a>
            <button onClick={() => navigate('/login')} className="px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:text-cyan-600 dark:hover:text-cyan-400">
              Sign in
            </button>
            <button
              onClick={() => navigate('/apply')}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium"
            >
              Apply
            </button>
            <ThemeToggle className="text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800" />
          </nav>
        </div>
      </header>

      {/* Hero */}
      <main>
        <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-slate-900 dark:text-white mb-6">
              Refer customers to OAK. <span className="text-cyan-600 dark:text-cyan-400">Earn ongoing commissions.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
              Join the OAK affiliate program and earn a percentage of every monthly plan payment from customers you refer — for as long as they're with us.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/apply')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold text-base shadow-sm"
              >
                Apply Now <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center justify-center px-8 py-4 border border-slate-300 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg font-semibold text-base"
              >
                Existing affiliate — Sign in
              </button>
            </div>
          </div>
        </section>

        {/* Two cards (down from three) — kept generic, no tier/override mentions */}
        <section className="bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                icon={DollarSign}
                title="Recurring Commissions"
                body="Earn a percentage of every monthly plan payment from your referrals — month after month, as long as they remain customers."
              />
              <Card
                icon={Users}
                title="Track Everything"
                body="Sign in to your portal to see your referrals, monthly earnings, payment history, and projected commissions in real time."
              />
            </div>
          </div>
        </section>

        {/* Secondary CTA */}
        <section className="max-w-6xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">
            Ready to start earning?
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            Apply in a few minutes. We'll review and follow up by email.
          </p>
          <button
            onClick={() => navigate('/apply')}
            className="inline-flex items-center gap-2 px-8 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-semibold"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </button>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 dark:bg-slate-950 text-slate-300 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="text-lg font-bold text-white mb-2">OAK Affiliates</div>
              <p className="text-sm text-slate-400">A program of Oasis Auto Konnect.</p>
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-3">Portal</div>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/apply')} className="hover:text-cyan-400">Apply</button></li>
                <li><button onClick={() => navigate('/login')} className="hover:text-cyan-400">Sign in</button></li>
                <li><button onClick={() => navigate('/forgot-password')} className="hover:text-cyan-400">Forgot password</button></li>
              </ul>
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-3">Main Site</div>
              <ul className="space-y-2 text-sm">
                <li><a href={MAIN_SITE} className="hover:text-cyan-400 flex items-center gap-1">Home <ExternalLink className="w-3 h-3" /></a></li>
                <li><a href={`${MAIN_SITE}/pages/features.html`} className="hover:text-cyan-400">Features</a></li>
                <li><a href={`${MAIN_SITE}/pages/pricing.html`} className="hover:text-cyan-400">Pricing</a></li>
                <li><a href={`${MAIN_SITE}/pages/contact.html`} className="hover:text-cyan-400">Contact</a></li>
              </ul>
            </div>
            <div>
              <div className="text-sm font-semibold text-white mb-3">Legal</div>
              <ul className="space-y-2 text-sm">
                <li><a href={`${MAIN_SITE}/pages/legal/Privacy-Policy.html`} className="hover:text-cyan-400">Privacy Policy</a></li>
                <li><a href={`${MAIN_SITE}/pages/legal/Terms-of-Service.html`} className="hover:text-cyan-400">Terms of Service</a></li>
                <li><a href={`${MAIN_SITE}/pages/legal/Acceptable-Use-Policy.html`} className="hover:text-cyan-400">Acceptable Use</a></li>
                <li><a href={`${MAIN_SITE}/pages/legal/Cookie-Policy.html`} className="hover:text-cyan-400">Cookie Policy</a></li>
                <li><a href={`${MAIN_SITE}/pages/legal/TCPA-Compliance-Addendum.html`} className="hover:text-cyan-400">TCPA Compliance</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-800 text-sm text-slate-400 flex flex-col sm:flex-row justify-between gap-2">
            <div>© {new Date().getFullYear()} Oasis Auto Konnect. All rights reserved.</div>
            <div>OAK Affiliate Program</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Card({ icon: Icon, title, body }: { icon: any; title: string; body: string }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-8 shadow-sm">
      <div className="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
      </div>
      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
      <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{body}</p>
    </div>
  );
}
